import { Component, OnInit, OnDestroy, HostListener } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Subject } from 'rxjs';
import Swal from 'sweetalert2';

import { SmartTemplateService } from '../smart-template.service';
import { SmartTemplateBuilderComponent } from '../smart-template-builder/smart-template-builder.component';
import { helper } from '../../../../../environments/helper';
import { environment } from '../../../../../environments/environment';
import { PermissionsService } from '../../../../services/permissions.service';
import { AuthService } from '../../../../services/auth.service';

declare var $: any;

export interface SmartTemplateData {
    id: number;
    name: string;
    description: string;
    type: string;
    type_label: string;
    category: string;
    category_id: number;
    active: boolean;
    active_label: string;
    is_public: boolean;
    priority: number;
    tags: string;
    creator: string;
    created_at: string;
    updated_at: string;
    actions: {
        edit: boolean;
        delete: boolean;
        view_parts: boolean;
        duplicate: boolean;
        toggle_status: boolean;
    };
}

@Component({
    selector: 'app-smart-template-management',
    templateUrl: './smart-template-management.component.html',
    styleUrls: ['./smart-template-management.component.scss']
})
export class SmartTemplateManagementComponent implements OnInit, OnDestroy {
    dtOptions: DataTables.Settings = {};
    dtTrigger: Subject<any> = new Subject<any>();
    isLoading = false;

    // Filters
    showCard = false;
    nameFilter = '';
    activeValue: any = '';
    typeValue = '';
    categoryValue = '';
    parameters = [
        { key: 'Toate', value: '' },
        { key: 'Activ', value: true },
        { key: 'Inactiv', value: false }
    ];

    templateTypes = [
        { key: 'Toate', value: '' },
        { key: 'Standard', value: 'standard' },
        { key: 'Contract', value: 'contract' },
        { key: 'Multi-pagină', value: 'multi-page' },
        { key: 'Workflow', value: 'workflow' }
    ];

    categories: any[] = [];

    public helper = helper;

    constructor(
        private smartTemplateService: SmartTemplateService,
        private dialog: MatDialog,
        public permissionsService: PermissionsService,
        public dialogRef: MatDialogRef<SmartTemplateManagementComponent>,
        private authService: AuthService
    ) { }

    ngOnInit(): void {
        // Initialize service with auth token
        this.authService.returnTokenUser().then((token) => {
            this.smartTemplateService.setToken(token);

            // Initialize data after token is set
            this.loadCategories();
            this.initializeDataTable();

            // Trigger the DataTable after a short delay to ensure it's properly initialized
            setTimeout(() => {
                this.dtTrigger.next(null);
            }, 100);
        });
    }

    ngOnDestroy(): void {
        this.dtTrigger.unsubscribe();
    }

    /**
     * Load categories for filters
     */
    loadCategories(): void {
        this.smartTemplateService.getCategories().subscribe(
            (response: any) => {
                if (response.success && response.data) {
                    this.categories = [
                        { id: '', name: 'Toate categoriile' },
                        ...response.data
                    ];
                }
            },
            (error) => {
                console.error('Error loading categories:', error);
            }
        );
    }

    /**
     * Initialize DataTable
     */
    initializeDataTable(): void {
        const self = this;

        this.dtOptions = {
            pagingType: 'simple_numbers',
            pageLength: 25,
            lengthMenu: [10, 25, 50, 100],
            serverSide: true,
            processing: true,
            searching: true,
            ordering: true,
            language: environment.config.romanianLanguageDataTable as any,
            ajax: (dataTablesParameters: any, callback: any) => {
                self.isLoading = true;

                // Add filter parameters
                const filters = {
                    ...dataTablesParameters,
                    active: this.activeValue,
                    type: this.typeValue,
                    category_id: this.categoryValue,
                    name: this.nameFilter
                };

                console.log('Making API call to load smart templates with filters:', filters);

                self.smartTemplateService.getSmartTemplatesList(filters).subscribe(
                    (response: any) => {
                        console.log('API response:', response);
                        self.isLoading = false;
                        if (response.success) {
                            callback({
                                recordsTotal: response.recordsTotal,
                                recordsFiltered: response.recordsFiltered,
                                data: response.data
                            });
                        } else {
                            console.error('Error loading smart templates:', response.message);
                            callback({
                                recordsTotal: 0,
                                recordsFiltered: 0,
                                data: []
                            });
                        }
                    },
                    (error) => {
                        self.isLoading = false;
                        console.error('Error loading smart templates:', error);
                        callback({
                            recordsTotal: 0,
                            recordsFiltered: 0,
                            data: []
                        });
                    }
                );
            },
            columns: [
                {
                    title: 'ID',
                    data: 'id',
                    width: '60px',
                    className: 'text-center',
                    orderable: true
                },
                {
                    title: 'Nume Template',
                    data: 'name',
                    orderable: true,
                    render: (data: any, type: any, row: SmartTemplateData) => {
                        let html = `<strong>${data}</strong>`;
                        if (row.description) {
                            html += `<br><small class="text-muted">${row.description}</small>`;
                        }
                        if (row.tags) {
                            html += `<br><span class="badge badge-info badge-sm">${row.tags}</span>`;
                        }
                        return html;
                    }
                },
                {
                    title: 'Tip',
                    data: 'type_label',
                    width: '100px',
                    className: 'text-center',
                    orderable: true,
                    render: (data: any, type: any, row: SmartTemplateData) => {
                        const badgeClass = row.type === 'contract' ? 'badge-warning' :
                            row.type === 'workflow' ? 'badge-info' :
                                row.type === 'multi-page' ? 'badge-secondary' : 'badge-primary';
                        return `<span class="badge ${badgeClass}">${data}</span>`;
                    }
                },
                {
                    title: 'Categorie',
                    data: 'category',
                    width: '120px',
                    orderable: true,
                    render: (data: any) => {
                        return data || 'N/A';
                    }
                },
                {
                    title: 'Status',
                    data: 'active_label',
                    width: '80px',
                    className: 'text-center',
                    orderable: true,
                    render: (data: any, type: any, row: SmartTemplateData) => {
                        const badgeClass = row.active ? 'badge-success' : 'badge-danger';
                        return `<span class="badge ${badgeClass}">${data}</span>`;
                    }
                },
                {
                    title: 'Prioritate',
                    data: 'priority',
                    width: '80px',
                    className: 'text-center',
                    orderable: true,
                    render: (data: any) => {
                        const stars = '★'.repeat(data || 2);
                        return `<span class="text-warning">${stars}</span>`;
                    }
                },
                {
                    title: 'Creator',
                    data: 'creator',
                    width: '100px',
                    orderable: false
                },
                {
                    title: 'Data Creare',
                    data: 'created_at',
                    width: '120px',
                    className: 'text-center',
                    orderable: true
                },
                {
                    title: 'Acțiuni',
                    data: null,
                    width: '200px',
                    className: 'text-center',
                    orderable: false,
                    render: (data: any, type: any, row: SmartTemplateData) => {
                        let actions = '';

                        if (row.actions.edit && self.permissionsService.hasPermission('dgaspc-module.settings.template-documente.edit')) {
                            actions += `<button type="button" class="btn btn-sm btn-warning me-1" onclick="window.editSmartTemplate(${row.id})" title="Editează">
                <i class="fa-solid fa-edit"></i>
              </button>`;
                        }

                        if (row.actions.duplicate && self.permissionsService.hasPermission('dgaspc-module.settings.template-documente.add')) {
                            actions += `<button type="button" class="btn btn-sm btn-info me-1" onclick="window.duplicateSmartTemplate(${row.id})" title="Duplică">
                <i class="fa-solid fa-copy"></i>
              </button>`;
                        }

                        if (row.actions.toggle_status && self.permissionsService.hasPermission('dgaspc-module.settings.template-documente.edit')) {
                            const toggleIcon = row.active ? 'fa-toggle-on' : 'fa-toggle-off';
                            const toggleClass = row.active ? 'btn-success' : 'btn-secondary';
                            actions += `<button type="button" class="btn btn-sm ${toggleClass} me-1" onclick="window.toggleSmartTemplateStatus(${row.id})" title="Schimbă status">
                <i class="fa-solid ${toggleIcon}"></i>
              </button>`;
                        }

                        if (row.actions.delete && self.permissionsService.hasPermission('dgaspc-module.settings.template-documente.delete')) {
                            actions += `<button type="button" class="btn btn-sm btn-danger me-1" onclick="window.deleteSmartTemplate(${row.id})" title="Șterge">
                <i class="fa-solid fa-trash"></i>
              </button>`;
                        }

                        return actions;
                    }
                }
            ],
            order: [[0, 'desc']],
            responsive: true,
            autoWidth: false,
            dom: '<"row"<"col-sm-6"l><"col-sm-6"f>>rtip'
        };

        // Setup global window functions for DataTable action buttons
        this.setupGlobalActions();
    }

    /**
     * Setup global action functions for DataTable buttons
     */
    setupGlobalActions(): void {
        const self = this;

        (window as any).editSmartTemplate = (id: number) => {
            self.editTemplate(id);
        };

        (window as any).duplicateSmartTemplate = (id: number) => {
            self.duplicateTemplate(id);
        };

        (window as any).toggleSmartTemplateStatus = (id: number) => {
            self.toggleTemplateStatus(id);
        };

        (window as any).deleteSmartTemplate = (id: number) => {
            self.deleteTemplate(id);
        };
    }

    /**
     * Create new smart template
     */
    createTemplate(): void {
        const dialogRef = this.dialog.open(SmartTemplateBuilderComponent, {
            width: '90%',
            maxWidth: '1200px',
            height: '90%',
            disableClose: true,
            data: { templateId: null },
            autoFocus: false,
        });

        dialogRef.afterClosed().subscribe(result => {
            if (result && result.success) {
                this.rerenderDT(false);
            }
        });
    }

    /**
     * Edit existing smart template
     */
    editTemplate(templateId: number): void {
        const dialogRef = this.dialog.open(SmartTemplateBuilderComponent, {
            width: '90%',
            maxWidth: '1200px',
            height: '90%',
            disableClose: true,
            data: { templateId: templateId }
        });

        dialogRef.afterClosed().subscribe(result => {
            if (result && result.success) {
                this.rerenderDT(false);
            }
        });
    }

    /**
     * Duplicate smart template
     */
    duplicateTemplate(templateId: number): void {
        Swal.fire({
            title: 'Confirmare duplicare',
            text: 'Sigur doriți să duplicați acest smart template?',
            icon: 'question',
            showCancelButton: true,
            confirmButtonText: 'Da, duplică',
            cancelButtonText: 'Anulează',
            reverseButtons: true,
        }).then((result) => {
            if (result.isConfirmed) {
                this.smartTemplateService.duplicateSmartTemplate(templateId).subscribe(
                    (response: any) => {
                        if (response.success) {
                            Swal.fire({
                                title: 'Succes',
                                text: response.message,
                                icon: 'success'
                            }).then(() => {
                                this.rerenderDT(false);
                            });
                        } else {
                            Swal.fire({
                                title: 'Eroare',
                                text: response.message || 'A apărut o eroare la duplicarea template-ului',
                                icon: 'error'
                            });
                        }
                    },
                    (error) => {
                        console.error('Error duplicating template:', error);
                        Swal.fire({
                            title: 'Eroare',
                            text: 'A apărut o eroare neașteptată la duplicarea template-ului',
                            icon: 'error'
                        });
                    }
                );
            }
        });
    }

    /**
     * Toggle template status (active/inactive)
     */
    toggleTemplateStatus(templateId: number): void {
        this.smartTemplateService.toggleSmartTemplateStatus(templateId).subscribe(
            (response: any) => {
                if (response.success) {
                    Swal.fire({
                        title: 'Succes',
                        text: response.message,
                        icon: 'success',
                        timer: 2000,
                        showConfirmButton: false
                    });
                    this.rerenderDT(false);
                } else {
                    Swal.fire({
                        title: 'Eroare',
                        text: response.message || 'A apărut o eroare la schimbarea statusului',
                        icon: 'error'
                    });
                }
            },
            (error) => {
                console.error('Error toggling template status:', error);
                Swal.fire({
                    title: 'Eroare',
                    text: 'A apărut o eroare neașteptată la schimbarea statusului',
                    icon: 'error'
                });
            }
        );
    }

    /**
     * Delete smart template
     */
    deleteTemplate(templateId: number): void {
        Swal.fire({
            title: 'Confirmare ștergere',
            text: 'Sigur doriți să ștergeți acest smart template? Această acțiune nu poate fi anulată.',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Da, șterge',
            cancelButtonText: 'Anulează',
            confirmButtonColor: '#d33',
            reverseButtons: true,
        }).then((result) => {
            if (result.isConfirmed) {
                this.smartTemplateService.deleteSmartTemplate(templateId).subscribe(
                    (response: any) => {
                        if (response.success) {
                            Swal.fire({
                                title: 'Succes',
                                text: response.message,
                                icon: 'success'
                            }).then(() => {
                                this.rerenderDT(false);
                            });
                        } else {
                            Swal.fire({
                                title: 'Eroare',
                                text: response.message || 'A apărut o eroare la ștergerea template-ului',
                                icon: 'error'
                            });
                        }
                    },
                    (error) => {
                        console.error('Error deleting template:', error);
                        let errorMessage = 'A apărut o eroare neașteptată la ștergerea template-ului';
                        if (error.error && error.error.message) {
                            errorMessage = error.error.message;
                        }
                        Swal.fire({
                            title: 'Eroare',
                            text: errorMessage,
                            icon: 'error'
                        });
                    }
                );
            }
        });
    }

    /**
     * Toggle filter card visibility
     */
    toggleCard(): void {
        this.showCard = !this.showCard;
    }

    /**
     * Apply filters and rerender DataTable
     */
    applyFilters(): void {
        this.rerenderDT(false);
    }

    /**
     * Reset all filters
     */
    resetForm(): void {
        this.nameFilter = '';
        this.activeValue = '';
        this.typeValue = '';
        this.categoryValue = '';
        this.rerenderDT(false);
    }

    /**
     * Rerender DataTable
     */
    rerenderDT(refresh: boolean = true): void {
        if (refresh) {
            this.dtTrigger.next(null);
        } else {
            const table = $('#datatable').DataTable();
            table.ajax.reload(null, false);
        }
    }

    /**
     * Close dialog
     */
    closeDialog(): void {
        this.dialogRef.close();
    }

    /**
     * Handle ESC key press to close dialog
     */
    @HostListener('document:keydown.escape', ['$event'])
    onEscapeKey(event: KeyboardEvent): void {
        this.closeDialog();
    }
}